import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { bindActionCreators } from "redux";
import FelicitacionLoading from "./Loading";
import FelicitacionDetail from "./Detail";
import FelicitacionFinish from "./finish/Finish";
import { felicitacionTotalSteps } from "./steps/config";
import { actionsCreators, State } from "../../redux";
import { BirthdayGreeting, type UserScheme } from "client-v2";

const BIRTHDAY_LOADING_MIN_MS = 2500;

const wait = (ms: number) =>
	new Promise((resolve) => window.setTimeout(resolve, ms));

const getGrandpaFromLinkResponse = (data: any) => {
	if (
		data &&
		typeof data === "object" &&
		"objectId" in data &&
		"type" in data &&
		data.type === "grandpa"
	) {
		return data as UserScheme;
	}

	return undefined;
};

const FelicitacionFlow = () => {
	const dispatch = useDispatch();
	const { setShowError } = bindActionCreators(actionsCreators, dispatch);
	const navigate = useNavigate();
	const location = useLocation();
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const [step, setStep] = useState<"loading" | "detail" | "finish">(
		"loading",
	);
	const [detailStep, setDetailStep] = useState(1);
	const [selectedGrandpa, setSelectedGrandpa] = useState<UserScheme>();
	const [isLoadingGrandpa, setIsLoadingGrandpa] = useState(false);

	const startFlow = useCallback(async () => {
		if (!currentUser) {
			navigate("/felicitacion/registro", {
				state: { autostart: true },
			});
			return;
		}

		setIsLoadingGrandpa(true);
		try {
			const loadingDelay = wait(BIRTHDAY_LOADING_MIN_MS);
			const { data } = await BirthdayGreeting.link({
				volunteerId: currentUser.objectId,
			});
			const selectedGrandpa = getGrandpaFromLinkResponse(data);

			if (!selectedGrandpa) {
				console.error(
					"BirthdayGreeting.link no devolvio un abuelo utilizable",
				);
				setShowError({
					show: true,
					message:
						"No hemos podido cargar la informacion del cumpleanero. Intentalo de nuevo.",
				});
				return;
			}

			await loadingDelay;

			setSelectedGrandpa(selectedGrandpa);
			setDetailStep(1);
			setStep("detail");
		} catch (error) {
			console.error("Error al obtener el cumpleanero", error);
			setShowError({
				show: true,
				message:
					error instanceof Error && error.message
						? error.message
						: "Ha ocurrido un error buscando al cumpleanero. Intentalo de nuevo.",
			});
		} finally {
			setIsLoadingGrandpa(false);
		}
	}, [currentUser, navigate, setShowError]);

	useEffect(() => {
		const shouldAutostart =
			(location.state as { autostart?: boolean } | undefined)?.autostart;

		if (step === "loading" && currentUser && shouldAutostart && !isLoadingGrandpa) {
			navigate(location.pathname, { replace: true, state: null });
			void startFlow();
		}
	}, [
		currentUser,
		isLoadingGrandpa,
		location.pathname,
		location.state,
		navigate,
		startFlow,
		step,
	]);

	if (step === "loading") {
		return (
			<FelicitacionLoading
				loading={isLoadingGrandpa}
				onContinue={startFlow}
			/>
		);
	}

	if (step === "finish") {
		return (
			<FelicitacionFinish
				selectedGrandpa={selectedGrandpa}
				onBack={() => {
					setDetailStep(felicitacionTotalSteps);
					setStep("detail");
				}}
				onContinue={() => {
					window.location.href =
						"https://www.adoptaunabuelo.org/proceso-donacion/";
				}}
			/>
		);
	}

	return (
		<FelicitacionDetail
			step={detailStep}
			selectedGrandpa={selectedGrandpa}
			onOpenProfile={() => setDetailStep(1)}
			onNext={() => {
				if (detailStep >= felicitacionTotalSteps) {
					setStep("finish");
					return;
				}
				setDetailStep((s) => Math.min(felicitacionTotalSteps, s + 1));
			}}
			onBack={() => {
				if (detailStep <= 1) {
					setStep("loading");
				} else {
					setDetailStep((s) => Math.max(1, s - 1));
				}
			}}
		/>
	);
};

export default FelicitacionFlow;
